/**
 * Full country + E.164 dial-code table for the pre-chat phone field's country
 * picker (`PhoneField` in `../screens/PreChatForm.tsx`).
 *
 * Every ISO-3166-1 country plus the common inhabited territories that carry
 * their own dial code are listed, sorted by name. Several entries intentionally
 * SHARE a dial code — NANP's `+1` for the US, Canada, and much of the Caribbean;
 * `+7` for Russia/Kazakhstan; `+44` for the UK, Isle of Man, Jersey, Guernsey;
 * `+212` for Morocco/Western Sahara; `+61` for Australia + its island
 * territories; `+590` for Guadeloupe/Saint Barthélemy/Saint Martin; `+599` for
 * Curaçao/Bonaire, Sint Eustatius and Saba — they stay distinguishable in the
 * UI by country name, which is exactly what the picker's search box filters on.
 *
 * Kept as plain data (no runtime deps) since this ships in the visitor-facing
 * widget bundle — see the bundle-size note in the PR description.
 */

export interface CountryDialCode {
  /** ISO 3166-1 alpha-2 code, e.g. "US". */
  iso2: string;
  /** Common English name, e.g. "United States". */
  name: string;
  /** E.164 calling code, WITH the leading "+", e.g. "+1". */
  dialCode: string;
}

const REGIONAL_INDICATOR_A = 0x1f1e6;

/**
 * Regional-indicator flag emoji for an ISO 3166-1 alpha-2 code (e.g. "IN" → 🇮🇳).
 *
 * Derived programmatically rather than stored per-entry: a flag emoji IS just a
 * pair of Unicode "regional indicator symbol" codepoints spelling the ISO code,
 * so computing it is one `String.fromCodePoint` call and avoids pasting ~240
 * emoji literals into this file. This mirrors the same derivation already used
 * for visitor geo flags in `dashboard/src/pages/chat/visitorMeta.tsx`
 * (`flagEmojiFromCountry`), which is a proven pattern in this codebase — flag
 * emoji rendering is broad in evergreen browsers/OSes, and a missing glyph
 * degrades gracefully to the two-letter tofu, never breaking layout.
 */
export function flagEmoji(iso2: string): string {
  const cc = iso2.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return '';
  return String.fromCodePoint(
    REGIONAL_INDICATOR_A + (cc.charCodeAt(0) - 65),
    REGIONAL_INDICATOR_A + (cc.charCodeAt(1) - 65),
  );
}

/** Default selected country for the phone field — parity with the old hardcoded "+1" default. */
export const DEFAULT_COUNTRY_ISO2 = 'US';

/** Every ISO-3166-1 country plus common inhabited territories, sorted by name. */
export const COUNTRIES: CountryDialCode[] = [
  { iso2: 'AF', name: 'Afghanistan', dialCode: '+93' },
  { iso2: 'AX', name: 'Åland Islands', dialCode: '+358' },
  { iso2: 'AL', name: 'Albania', dialCode: '+355' },
  { iso2: 'DZ', name: 'Algeria', dialCode: '+213' },
  { iso2: 'AS', name: 'American Samoa', dialCode: '+1' },
  { iso2: 'AD', name: 'Andorra', dialCode: '+376' },
  { iso2: 'AO', name: 'Angola', dialCode: '+244' },
  { iso2: 'AI', name: 'Anguilla', dialCode: '+1' },
  { iso2: 'AG', name: 'Antigua and Barbuda', dialCode: '+1' },
  { iso2: 'AR', name: 'Argentina', dialCode: '+54' },
  { iso2: 'AM', name: 'Armenia', dialCode: '+374' },
  { iso2: 'AW', name: 'Aruba', dialCode: '+297' },
  { iso2: 'AU', name: 'Australia', dialCode: '+61' },
  { iso2: 'AT', name: 'Austria', dialCode: '+43' },
  { iso2: 'AZ', name: 'Azerbaijan', dialCode: '+994' },
  { iso2: 'BS', name: 'Bahamas', dialCode: '+1' },
  { iso2: 'BH', name: 'Bahrain', dialCode: '+973' },
  { iso2: 'BD', name: 'Bangladesh', dialCode: '+880' },
  { iso2: 'BB', name: 'Barbados', dialCode: '+1' },
  { iso2: 'BY', name: 'Belarus', dialCode: '+375' },
  { iso2: 'BE', name: 'Belgium', dialCode: '+32' },
  { iso2: 'BZ', name: 'Belize', dialCode: '+501' },
  { iso2: 'BJ', name: 'Benin', dialCode: '+229' },
  { iso2: 'BM', name: 'Bermuda', dialCode: '+1' },
  { iso2: 'BT', name: 'Bhutan', dialCode: '+975' },
  { iso2: 'BO', name: 'Bolivia', dialCode: '+591' },
  { iso2: 'BQ', name: 'Bonaire, Sint Eustatius and Saba', dialCode: '+599' },
  { iso2: 'BA', name: 'Bosnia and Herzegovina', dialCode: '+387' },
  { iso2: 'BW', name: 'Botswana', dialCode: '+267' },
  { iso2: 'BR', name: 'Brazil', dialCode: '+55' },
  { iso2: 'IO', name: 'British Indian Ocean Territory', dialCode: '+246' },
  { iso2: 'VG', name: 'British Virgin Islands', dialCode: '+1' },
  { iso2: 'BN', name: 'Brunei', dialCode: '+673' },
  { iso2: 'BG', name: 'Bulgaria', dialCode: '+359' },
  { iso2: 'BF', name: 'Burkina Faso', dialCode: '+226' },
  { iso2: 'BI', name: 'Burundi', dialCode: '+257' },
  { iso2: 'KH', name: 'Cambodia', dialCode: '+855' },
  { iso2: 'CM', name: 'Cameroon', dialCode: '+237' },
  { iso2: 'CA', name: 'Canada', dialCode: '+1' },
  { iso2: 'CV', name: 'Cape Verde', dialCode: '+238' },
  { iso2: 'KY', name: 'Cayman Islands', dialCode: '+1' },
  { iso2: 'CF', name: 'Central African Republic', dialCode: '+236' },
  { iso2: 'TD', name: 'Chad', dialCode: '+235' },
  { iso2: 'CL', name: 'Chile', dialCode: '+56' },
  { iso2: 'CN', name: 'China', dialCode: '+86' },
  { iso2: 'CX', name: 'Christmas Island', dialCode: '+61' },
  { iso2: 'CC', name: 'Cocos (Keeling) Islands', dialCode: '+61' },
  { iso2: 'CO', name: 'Colombia', dialCode: '+57' },
  { iso2: 'KM', name: 'Comoros', dialCode: '+269' },
  { iso2: 'CD', name: 'Congo (DRC)', dialCode: '+243' },
  { iso2: 'CG', name: 'Congo (Republic)', dialCode: '+242' },
  { iso2: 'CK', name: 'Cook Islands', dialCode: '+682' },
  { iso2: 'CR', name: 'Costa Rica', dialCode: '+506' },
  { iso2: 'CI', name: "Côte d'Ivoire", dialCode: '+225' },
  { iso2: 'HR', name: 'Croatia', dialCode: '+385' },
  { iso2: 'CU', name: 'Cuba', dialCode: '+53' },
  { iso2: 'CW', name: 'Curaçao', dialCode: '+599' },
  { iso2: 'CY', name: 'Cyprus', dialCode: '+357' },
  { iso2: 'CZ', name: 'Czechia', dialCode: '+420' },
  { iso2: 'DK', name: 'Denmark', dialCode: '+45' },
  { iso2: 'DJ', name: 'Djibouti', dialCode: '+253' },
  { iso2: 'DM', name: 'Dominica', dialCode: '+1' },
  { iso2: 'DO', name: 'Dominican Republic', dialCode: '+1' },
  { iso2: 'EC', name: 'Ecuador', dialCode: '+593' },
  { iso2: 'EG', name: 'Egypt', dialCode: '+20' },
  { iso2: 'SV', name: 'El Salvador', dialCode: '+503' },
  { iso2: 'GQ', name: 'Equatorial Guinea', dialCode: '+240' },
  { iso2: 'ER', name: 'Eritrea', dialCode: '+291' },
  { iso2: 'EE', name: 'Estonia', dialCode: '+372' },
  { iso2: 'SZ', name: 'Eswatini', dialCode: '+268' },
  { iso2: 'ET', name: 'Ethiopia', dialCode: '+251' },
  { iso2: 'FK', name: 'Falkland Islands', dialCode: '+500' },
  { iso2: 'FO', name: 'Faroe Islands', dialCode: '+298' },
  { iso2: 'FJ', name: 'Fiji', dialCode: '+679' },
  { iso2: 'FI', name: 'Finland', dialCode: '+358' },
  { iso2: 'FR', name: 'France', dialCode: '+33' },
  { iso2: 'GF', name: 'French Guiana', dialCode: '+594' },
  { iso2: 'PF', name: 'French Polynesia', dialCode: '+689' },
  { iso2: 'GA', name: 'Gabon', dialCode: '+241' },
  { iso2: 'GM', name: 'Gambia', dialCode: '+220' },
  { iso2: 'GE', name: 'Georgia', dialCode: '+995' },
  { iso2: 'DE', name: 'Germany', dialCode: '+49' },
  { iso2: 'GH', name: 'Ghana', dialCode: '+233' },
  { iso2: 'GI', name: 'Gibraltar', dialCode: '+350' },
  { iso2: 'GR', name: 'Greece', dialCode: '+30' },
  { iso2: 'GL', name: 'Greenland', dialCode: '+299' },
  { iso2: 'GD', name: 'Grenada', dialCode: '+1' },
  { iso2: 'GP', name: 'Guadeloupe', dialCode: '+590' },
  { iso2: 'GU', name: 'Guam', dialCode: '+1' },
  { iso2: 'GT', name: 'Guatemala', dialCode: '+502' },
  { iso2: 'GG', name: 'Guernsey', dialCode: '+44' },
  { iso2: 'GN', name: 'Guinea', dialCode: '+224' },
  { iso2: 'GW', name: 'Guinea-Bissau', dialCode: '+245' },
  { iso2: 'GY', name: 'Guyana', dialCode: '+592' },
  { iso2: 'HT', name: 'Haiti', dialCode: '+509' },
  { iso2: 'HN', name: 'Honduras', dialCode: '+504' },
  { iso2: 'HK', name: 'Hong Kong', dialCode: '+852' },
  { iso2: 'HU', name: 'Hungary', dialCode: '+36' },
  { iso2: 'IS', name: 'Iceland', dialCode: '+354' },
  { iso2: 'IN', name: 'India', dialCode: '+91' },
  { iso2: 'ID', name: 'Indonesia', dialCode: '+62' },
  { iso2: 'IR', name: 'Iran', dialCode: '+98' },
  { iso2: 'IQ', name: 'Iraq', dialCode: '+964' },
  { iso2: 'IE', name: 'Ireland', dialCode: '+353' },
  { iso2: 'IM', name: 'Isle of Man', dialCode: '+44' },
  { iso2: 'IL', name: 'Israel', dialCode: '+972' },
  { iso2: 'IT', name: 'Italy', dialCode: '+39' },
  { iso2: 'JM', name: 'Jamaica', dialCode: '+1' },
  { iso2: 'JP', name: 'Japan', dialCode: '+81' },
  { iso2: 'JE', name: 'Jersey', dialCode: '+44' },
  { iso2: 'JO', name: 'Jordan', dialCode: '+962' },
  { iso2: 'KZ', name: 'Kazakhstan', dialCode: '+7' },
  { iso2: 'KE', name: 'Kenya', dialCode: '+254' },
  { iso2: 'KI', name: 'Kiribati', dialCode: '+686' },
  { iso2: 'XK', name: 'Kosovo', dialCode: '+383' },
  { iso2: 'KW', name: 'Kuwait', dialCode: '+965' },
  { iso2: 'KG', name: 'Kyrgyzstan', dialCode: '+996' },
  { iso2: 'LA', name: 'Laos', dialCode: '+856' },
  { iso2: 'LV', name: 'Latvia', dialCode: '+371' },
  { iso2: 'LB', name: 'Lebanon', dialCode: '+961' },
  { iso2: 'LS', name: 'Lesotho', dialCode: '+266' },
  { iso2: 'LR', name: 'Liberia', dialCode: '+231' },
  { iso2: 'LY', name: 'Libya', dialCode: '+218' },
  { iso2: 'LI', name: 'Liechtenstein', dialCode: '+423' },
  { iso2: 'LT', name: 'Lithuania', dialCode: '+370' },
  { iso2: 'LU', name: 'Luxembourg', dialCode: '+352' },
  { iso2: 'MO', name: 'Macau', dialCode: '+853' },
  { iso2: 'MG', name: 'Madagascar', dialCode: '+261' },
  { iso2: 'MW', name: 'Malawi', dialCode: '+265' },
  { iso2: 'MY', name: 'Malaysia', dialCode: '+60' },
  { iso2: 'MV', name: 'Maldives', dialCode: '+960' },
  { iso2: 'ML', name: 'Mali', dialCode: '+223' },
  { iso2: 'MT', name: 'Malta', dialCode: '+356' },
  { iso2: 'MH', name: 'Marshall Islands', dialCode: '+692' },
  { iso2: 'MQ', name: 'Martinique', dialCode: '+596' },
  { iso2: 'MR', name: 'Mauritania', dialCode: '+222' },
  { iso2: 'MU', name: 'Mauritius', dialCode: '+230' },
  { iso2: 'YT', name: 'Mayotte', dialCode: '+262' },
  { iso2: 'MX', name: 'Mexico', dialCode: '+52' },
  { iso2: 'FM', name: 'Micronesia', dialCode: '+691' },
  { iso2: 'MD', name: 'Moldova', dialCode: '+373' },
  { iso2: 'MC', name: 'Monaco', dialCode: '+377' },
  { iso2: 'MN', name: 'Mongolia', dialCode: '+976' },
  { iso2: 'ME', name: 'Montenegro', dialCode: '+382' },
  { iso2: 'MS', name: 'Montserrat', dialCode: '+1' },
  { iso2: 'MA', name: 'Morocco', dialCode: '+212' },
  { iso2: 'MZ', name: 'Mozambique', dialCode: '+258' },
  { iso2: 'MM', name: 'Myanmar', dialCode: '+95' },
  { iso2: 'NA', name: 'Namibia', dialCode: '+264' },
  { iso2: 'NR', name: 'Nauru', dialCode: '+674' },
  { iso2: 'NP', name: 'Nepal', dialCode: '+977' },
  { iso2: 'NL', name: 'Netherlands', dialCode: '+31' },
  { iso2: 'NC', name: 'New Caledonia', dialCode: '+687' },
  { iso2: 'NZ', name: 'New Zealand', dialCode: '+64' },
  { iso2: 'NI', name: 'Nicaragua', dialCode: '+505' },
  { iso2: 'NE', name: 'Niger', dialCode: '+227' },
  { iso2: 'NG', name: 'Nigeria', dialCode: '+234' },
  { iso2: 'NU', name: 'Niue', dialCode: '+683' },
  { iso2: 'NF', name: 'Norfolk Island', dialCode: '+672' },
  { iso2: 'KP', name: 'North Korea', dialCode: '+850' },
  { iso2: 'MK', name: 'North Macedonia', dialCode: '+389' },
  { iso2: 'MP', name: 'Northern Mariana Islands', dialCode: '+1' },
  { iso2: 'NO', name: 'Norway', dialCode: '+47' },
  { iso2: 'OM', name: 'Oman', dialCode: '+968' },
  { iso2: 'PK', name: 'Pakistan', dialCode: '+92' },
  { iso2: 'PW', name: 'Palau', dialCode: '+680' },
  { iso2: 'PS', name: 'Palestine', dialCode: '+970' },
  { iso2: 'PA', name: 'Panama', dialCode: '+507' },
  { iso2: 'PG', name: 'Papua New Guinea', dialCode: '+675' },
  { iso2: 'PY', name: 'Paraguay', dialCode: '+595' },
  { iso2: 'PE', name: 'Peru', dialCode: '+51' },
  { iso2: 'PH', name: 'Philippines', dialCode: '+63' },
  { iso2: 'PL', name: 'Poland', dialCode: '+48' },
  { iso2: 'PT', name: 'Portugal', dialCode: '+351' },
  { iso2: 'PR', name: 'Puerto Rico', dialCode: '+1' },
  { iso2: 'QA', name: 'Qatar', dialCode: '+974' },
  { iso2: 'RE', name: 'Réunion', dialCode: '+262' },
  { iso2: 'RO', name: 'Romania', dialCode: '+40' },
  { iso2: 'RU', name: 'Russia', dialCode: '+7' },
  { iso2: 'RW', name: 'Rwanda', dialCode: '+250' },
  { iso2: 'BL', name: 'Saint Barthélemy', dialCode: '+590' },
  { iso2: 'SH', name: 'Saint Helena', dialCode: '+290' },
  { iso2: 'KN', name: 'Saint Kitts and Nevis', dialCode: '+1' },
  { iso2: 'LC', name: 'Saint Lucia', dialCode: '+1' },
  { iso2: 'MF', name: 'Saint Martin', dialCode: '+590' },
  { iso2: 'PM', name: 'Saint Pierre and Miquelon', dialCode: '+508' },
  { iso2: 'VC', name: 'Saint Vincent and the Grenadines', dialCode: '+1' },
  { iso2: 'WS', name: 'Samoa', dialCode: '+685' },
  { iso2: 'SM', name: 'San Marino', dialCode: '+378' },
  { iso2: 'ST', name: 'São Tomé and Príncipe', dialCode: '+239' },
  { iso2: 'SA', name: 'Saudi Arabia', dialCode: '+966' },
  { iso2: 'SN', name: 'Senegal', dialCode: '+221' },
  { iso2: 'RS', name: 'Serbia', dialCode: '+381' },
  { iso2: 'SC', name: 'Seychelles', dialCode: '+248' },
  { iso2: 'SL', name: 'Sierra Leone', dialCode: '+232' },
  { iso2: 'SG', name: 'Singapore', dialCode: '+65' },
  { iso2: 'SX', name: 'Sint Maarten', dialCode: '+1' },
  { iso2: 'SK', name: 'Slovakia', dialCode: '+421' },
  { iso2: 'SI', name: 'Slovenia', dialCode: '+386' },
  { iso2: 'SB', name: 'Solomon Islands', dialCode: '+677' },
  { iso2: 'SO', name: 'Somalia', dialCode: '+252' },
  { iso2: 'ZA', name: 'South Africa', dialCode: '+27' },
  { iso2: 'KR', name: 'South Korea', dialCode: '+82' },
  { iso2: 'SS', name: 'South Sudan', dialCode: '+211' },
  { iso2: 'ES', name: 'Spain', dialCode: '+34' },
  { iso2: 'LK', name: 'Sri Lanka', dialCode: '+94' },
  { iso2: 'SD', name: 'Sudan', dialCode: '+249' },
  { iso2: 'SR', name: 'Suriname', dialCode: '+597' },
  { iso2: 'SJ', name: 'Svalbard and Jan Mayen', dialCode: '+47' },
  { iso2: 'SE', name: 'Sweden', dialCode: '+46' },
  { iso2: 'CH', name: 'Switzerland', dialCode: '+41' },
  { iso2: 'SY', name: 'Syria', dialCode: '+963' },
  { iso2: 'TW', name: 'Taiwan', dialCode: '+886' },
  { iso2: 'TJ', name: 'Tajikistan', dialCode: '+992' },
  { iso2: 'TZ', name: 'Tanzania', dialCode: '+255' },
  { iso2: 'TH', name: 'Thailand', dialCode: '+66' },
  { iso2: 'TL', name: 'Timor-Leste', dialCode: '+670' },
  { iso2: 'TG', name: 'Togo', dialCode: '+228' },
  { iso2: 'TK', name: 'Tokelau', dialCode: '+690' },
  { iso2: 'TO', name: 'Tonga', dialCode: '+676' },
  { iso2: 'TT', name: 'Trinidad and Tobago', dialCode: '+1' },
  { iso2: 'TN', name: 'Tunisia', dialCode: '+216' },
  { iso2: 'TR', name: 'Turkey', dialCode: '+90' },
  { iso2: 'TM', name: 'Turkmenistan', dialCode: '+993' },
  { iso2: 'TC', name: 'Turks and Caicos Islands', dialCode: '+1' },
  { iso2: 'TV', name: 'Tuvalu', dialCode: '+688' },
  { iso2: 'UG', name: 'Uganda', dialCode: '+256' },
  { iso2: 'UA', name: 'Ukraine', dialCode: '+380' },
  { iso2: 'AE', name: 'United Arab Emirates', dialCode: '+971' },
  { iso2: 'GB', name: 'United Kingdom', dialCode: '+44' },
  { iso2: 'US', name: 'United States', dialCode: '+1' },
  { iso2: 'UY', name: 'Uruguay', dialCode: '+598' },
  { iso2: 'VI', name: 'US Virgin Islands', dialCode: '+1' },
  { iso2: 'UZ', name: 'Uzbekistan', dialCode: '+998' },
  { iso2: 'VU', name: 'Vanuatu', dialCode: '+678' },
  { iso2: 'VA', name: 'Vatican City', dialCode: '+379' },
  { iso2: 'VE', name: 'Venezuela', dialCode: '+58' },
  { iso2: 'VN', name: 'Vietnam', dialCode: '+84' },
  { iso2: 'WF', name: 'Wallis and Futuna', dialCode: '+681' },
  { iso2: 'EH', name: 'Western Sahara', dialCode: '+212' },
  { iso2: 'YE', name: 'Yemen', dialCode: '+967' },
  { iso2: 'ZM', name: 'Zambia', dialCode: '+260' },
  { iso2: 'ZW', name: 'Zimbabwe', dialCode: '+263' },
];
